import AppConfig from '../../AppConfig';
import { inferFileTypeFromName } from './cloudProvider';

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';
const GRAPH_SCOPES = ['Files.Read.All'];
const CHILDREN_PAGE_SIZE = 200;

const buildPickerUrl = (baseUrl) =>
  baseUrl.includes('onedrive.live.com')
    ? `${baseUrl}/picker`
    : `${baseUrl}/_layouts/15/FilePicker.aspx`;

let cachedBaseUrl = null;

const resolveUserBaseUrl = async (graphToken) => {
  if (cachedBaseUrl) return cachedBaseUrl;

  const override = import.meta.env.VITE_ONEDRIVE_BASE_URL;
  if (override) {
    cachedBaseUrl = override.replace(/\/$/, '');
    return cachedBaseUrl;
  }

  const response = await fetch(`${GRAPH_BASE_URL}/me/drive?$select=webUrl`, {
    headers: { Authorization: `Bearer ${graphToken}` }
  });
  if (!response.ok) throw new Error(`Could not resolve the user's OneDrive host (${response.status})`);
  const drive = await response.json();
  cachedBaseUrl = new URL(drive.webUrl).origin;
  return cachedBaseUrl;
};

let channelCounter = 0;
const nextChannelId = () => `edp-onedrive-${Date.now()}-${(channelCounter += 1)}`;

let msalInstancePromise = null;

const getMsalInstance = async () => {
  if (!msalInstancePromise) {
    msalInstancePromise = import('@azure/msal-browser').then(async ({ PublicClientApplication }) => {
      const instance = new PublicClientApplication({
        auth: {
          clientId: AppConfig.OneDriveClientId,
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: `${window.location.origin}/onedrive-callback.html`
        },
        cache: { cacheLocation: 'sessionStorage' }
      });
      await instance.initialize();
      return instance;
    });
  }
  return msalInstancePromise;
};

const acquireToken = async (scopes) => {
  const instance = await getMsalInstance();
  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];

  if (account) {
    try {
      const silent = await instance.acquireTokenSilent({ scopes, account });
      return silent.accessToken;
    } catch {
      // Silent acquisition failed, fall back to the interactive popup below
    }
  }

  const result = await instance.acquireTokenPopup({ scopes });
  instance.setActiveAccount(result.account);
  return result.accessToken;
};

const getGraphToken = () => acquireToken(GRAPH_SCOPES);

const getPickerToken = ({ resource, baseUrl }) => {
  const base = (resource || baseUrl).replace(/\/$/, '');
  return acquireToken([`${base}/.default`]);
};

const submitPickerForm = ({ win, baseUrl, params, token }) => {
  const queryString = new URLSearchParams({ filePicker: JSON.stringify(params) }).toString();
  const url = `${buildPickerUrl(baseUrl)}?${queryString}`;

  const form = win.document.createElement('form');
  form.setAttribute('action', url);
  form.setAttribute('method', 'POST');

  const tokenInput = win.document.createElement('input');
  tokenInput.setAttribute('type', 'hidden');
  tokenInput.setAttribute('name', 'access_token');
  tokenInput.setAttribute('value', token);
  form.appendChild(tokenInput);

  win.document.body.appendChild(form);
  form.submit();
};

export const openPicker = ({ multiselect = true, allowFolders = false } = {}) => {
  const pickerWindow = window.open('', 'OneDrivePicker', 'width=1080,height=720');
  if (!pickerWindow) {
    return Promise.reject(new Error('Could not open the OneDrive picker window (popup blocked?)'));
  }

  const channelId = nextChannelId();
  let port = null;
  let settled = false;
  let baseUrl = null;

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('message', onWindowMessage);
      if (port) port.close();
      if (pickerWindow && !pickerWindow.closed) pickerWindow.close();
    };

    const finish = (items) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(items);
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const onPortMessage = async (event) => {
      const message = event.data;
      if (message?.type === 'command') {
        port.postMessage({ type: 'acknowledge', id: message.id });
        const command = message.data;
        try {
          switch (command.command) {
            case 'authenticate': {
              const token = await getPickerToken({ resource: command.resource, baseUrl });
              port.postMessage({ type: 'result', id: message.id, data: { result: 'token', token } });
              break;
            }
            case 'close':
              finish([]);
              break;
            case 'pick':
              port.postMessage({ type: 'result', id: message.id, data: { result: 'success' } });
              finish(command.items || []);
              break;
            default:
              port.postMessage({
                type: 'result',
                id: message.id,
                data: { result: 'error', error: { code: 'unsupportedCommand', message: command.command } }
              });
          }
        } catch (error) {
          fail(error);
        }
      }
    };

    const onWindowMessage = (event) => {
      if (event.source !== pickerWindow) return;
      const message = event.data;
      if (message?.type === 'initialize' && message?.channelId === channelId) {
        [port] = event.ports;
        port.addEventListener('message', onPortMessage);
        port.start();
        port.postMessage({ type: 'activate' });
      }
    };

    window.addEventListener('message', onWindowMessage);

    (async () => {
      const graphToken = await getGraphToken();
      baseUrl = await resolveUserBaseUrl(graphToken);
      const pickerToken = await getPickerToken({ baseUrl });
      const params = {
        sdk: '8.0',
        entry: { oneDrive: {} },
        authentication: {},
        messaging: { origin: window.location.origin, channelId },
        selection: { mode: multiselect ? 'multiple' : 'single' },
        typesAndSources: {
          mode: allowFolders ? 'all' : 'files',
          pivots: { oneDrive: true, recent: true }
        }
      };
      submitPickerForm({ win: pickerWindow, baseUrl, params, token: pickerToken });
    })().catch(fail);
  });
};

const isFolder = (item) => Boolean(item?.folder);

const resolveDownloadUrl = async ({ driveId, itemId, graphToken }) => {
  const url = `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}`;
  const response = await fetch(url, { headers: { Authorization: `Bearer ${graphToken}` } });
  if (!response.ok) throw new Error(`Graph item lookup failed (${response.status})`);
  const item = await response.json();
  return item['@microsoft.graph.downloadUrl'];
};

const listFilesRecursively = async ({ driveId, itemId, graphToken }) => {
  const files = [];
  let nextUrl =
    `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}/children?$top=${CHILDREN_PAGE_SIZE}`;

  while (nextUrl) {
    const response = await fetch(nextUrl, { headers: { Authorization: `Bearer ${graphToken}` } });
    if (!response.ok) throw new Error(`Graph children lookup failed (${response.status})`);
    const page = await response.json();

    for (const child of page.value || []) {
      if (isFolder(child)) {
        const nested = await listFilesRecursively({ driveId, itemId: child.id, graphToken });
        files.push(...nested);
      } else {
        files.push({ ...child, parentReference: { driveId } });
      }
    }
    nextUrl = page['@odata.nextLink'] || null;
  }
  return files;
};

const downloadEntryAsFile = async ({ entry, graphToken }) => {
  const driveId = entry.parentReference?.driveId;
  const downloadUrl =
    entry['@microsoft.graph.downloadUrl'] ||
    (await resolveDownloadUrl({ driveId, itemId: entry.id, graphToken }));

  if (!downloadUrl) throw new Error(`No download URL resolved for "${entry.name}"`);

  const response = await fetch(downloadUrl);
  if (!response.ok) throw new Error(`Failed to download "${entry.name}" (${response.status})`);
  const blob = await response.blob();
  return new File([blob], entry.name, { type: inferFileTypeFromName(entry.name) || blob.type });
};

const settleToFiles = async (tasks) => {
  const results = await Promise.allSettled(tasks);
  const failures = results.filter((result) => result.status === 'rejected');
  if (failures.length) {
    console.error('[OneDrive] some items failed to download:', failures.map((f) => f.reason?.message));
  }
  return results
    .filter((result) => result.status === 'fulfilled')
    .flatMap((result) => result.value);
};

export const fetchOneDriveFilesAsLocal = async (selection) => {
  const graphToken = await getGraphToken();
  return settleToFiles(
    (selection || []).map(async (item) => [
      await downloadEntryAsFile({ entry: item, graphToken })
    ])
  );
};

export const fetchOneDriveSelectionAsFiles = async (selection) => {
  const graphToken = await getGraphToken();

  return settleToFiles(
    (selection || []).map(async (item) => {
      if (!isFolder(item)) {
        return [await downloadEntryAsFile({ entry: item, graphToken })];
      }
      const driveId = item.parentReference?.driveId;
      const files = await listFilesRecursively({ driveId, itemId: item.id, graphToken });
      return Promise.all(files.map((entry) => downloadEntryAsFile({ entry, graphToken })));
    })
  );
};

export const loadOneDrivePicker = async () => {
  await getMsalInstance();
  return { isSupported: typeof window !== 'undefined' && Boolean(AppConfig.OneDriveClientId) };
};
